<template>
  <div
    :class="['zg-tooltip', {
      [`zg-tooltip--background-${background}`]: background,
      [`zg-tooltip--position-${position}`]: position,
      [`zg-tooltip--size-${size}`]: size,
      'zg-tooltip--input': input,
      'zg-tooltip--input-with-trigger': input && !hideTrigger,
      'zg-tooltip--input-without-trigger': input && hideTrigger,
      'zg-tooltip--has-trigger': !hideTrigger,
      'zg-tooltip--visible': show,
      'zg-tooltip--single-line': singleLine,
      'zg-tooltip--black-trigger': blackTrigger
    }]"
    @click="toggleDisplay"
  >
    <button
      v-if="!hideTrigger"
      type="button"
      :class="['trigger', {
        'trigger--visible': show
      }]"
    >
      <span class="visually-hidden">
        More info about {{ title }}
      </span>

      <span aria-hidden="true">
        <zg-icon
          icon="tooltip-question-mark"
          size="small"
        />
      </span>
    </button>

    <transition name="fade-down">
      <div
        v-if="show"
        class="content"
        role="dialog"
        :aria-labelledby="title"
        :aria-describedby="text"
      >
        <div class="tooltip-arrow">
          <zg-icon
            icon="tooltip-arrow"
            size="small"
          />
        </div>

        <div class="message">
          <zg-close-button
            v-if="!hideTrigger"
            size="xsmall"
            class="close"
          />

          <h6
            v-if="title && !singleLine"
            :class="{ 'with-margin': text }"
          >
            {{ title }}
          </h6>

          <p
            v-if="text"
            v-html="text"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import ZgCloseButton from '../ZgCloseButton/ZgCloseButton.vue'
  import ZgIcon from '../ZgIcon/ZgIcon.vue'

  export default {
    name: 'ZgTooltip',
    components: {
      ZgCloseButton,
      ZgIcon
    },
    props: {
      title: {
        type: String,
        required: false,
        default: null
      },
      text: {
        type: String,
        required: false,
        default: null
      },
      background: {
        type: String,
        required: false,
        default: 'secondary-500',
        validator: value => [
          'beige',
          'secondary-500'
        ].includes(value)
      },
      size: {
        type: String,
        required: false,
        default: 'medium',
        validator: value => [
          'small',
          'medium'
        ].includes(value)
      },
      position: {
        type: String,
        required: false,
        default: 'top-left',
        validator: value => [
          'left-top',
          'left-bottom',
          'left-center',
          'right-top',
          'right-bottom',
          'right-center',
          'top-right',
          'top-left',
          'top-center',
          'bottom-right',
          'bottom-left',
          'bottom-center'
        ].includes(value)
      },
      input: {
        type: Boolean,
        required: false,
        default: null
      },
      hideTrigger: {
        type: Boolean,
        required: false,
        default: false
      },
      singleLine: {
        type: Boolean,
        required: false,
        default: false
      },
      visible: {
        type: Boolean,
        required: false,
        default: false
      },
      blackTrigger: {
        type: Boolean,
        required: false,
        default: false
      }
    },
    data: () => ({
      display: false
    }),
    computed: {
      show () {
        return this.display || (this.hideTrigger && this.visible)
      }
    },
    mounted () {
      window.addEventListener('click', this.handleClick)
    },
    beforeDestroy () {
      window.removeEventListener('click', this.handleClick)
    },
    methods: {
      toggleDisplay () {
        this.display = !this.display
      },
      handleClick (e) {
        const target = e.target
        const el = this.$el
        const parentEl = this.$parent.$el

        if (el !== target && !el.contains(target) && !parentEl.contains(target)) {
          this.display = false
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  $min-width: rem(296px);

  .zg-tooltip {
    position: relative;
    display: inline-flex;
    width: auto;

    // ? ----------------------------------------------------------------------
    // trigger
    // ? ----------------------------------------------------------------------

    .trigger {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
      cursor: pointer;
      outline: 0;
      border: none;
      background: transparent;
      visibility: visible;
      padding: 0;
      margin: 0;

      span[aria-hidden] {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        color: color(gray-500);
        border: 2px solid color(gray-500);
        border-radius: 100%;
        transition: all 0.3s ease;
      }

      ::v-deep svg {
        color: color(gray-500);
        width: 80%;
        height: 80%;
        transition: all 0.3s ease;
      }

      &:focus:not(.trigger--visible) {
        span[aria-hidden] {
          color: color(black);
          border-color: color(black);
          box-shadow: 0 0 0 6px color(brand-100);
        }

        ::v-deep svg {
          color: color(black);
        }
      }

      &:hover:not(.trigger--visible) {
        span[aria-hidden] {
          color: color(brand-300);
          border-color: color(brand-300);
        }

        ::v-deep svg {
          color: color(brand-300);
        }
      }

      &--visible {
        span[aria-hidden] {
          color: color(black);
          border-color: color(black);
        }

        ::v-deep svg {
          color: color(black);
        }
      }
    }

    // ? ----------------------------------------------------------------------
    // content
    // ? ----------------------------------------------------------------------

    .content {
      position: absolute;
      display: flex;
      align-items: center;
      min-width: $min-width;
      border-radius: rem(20px);
      z-index: 20;
      transition: all 0.2s ease-in-out;

      .tooltip-arrow {
        position: absolute;
        width: rem(16px);
        height: rem(8px);
      }

      .close {
        position: absolute;
        top: rhythm(small) - rem(5px);
        right: rhythm(small) - rem(5px);
      }

      .message {
        position: relative;
        width: 100%;
        height: 100%;
        padding: rhythm();

        h6 {
          font-size: rem(16px);
          line-height: rem(22px);
          margin: 0;

          &.with-margin {
            margin: 0 0 rem(4px);
          }
        }

        p {
          font-size: rem(14px);
          line-height: rem(22px);
          overflow-wrap: break-word;
          cursor: default;
          margin: 0;
        }

        ::v-deep a {
          color: color(black);
          border-color: color(black);
          cursor: pointer;
        }
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // size
  // ? ----------------------------------------------------------------------

  .zg-tooltip--size-small {
    .trigger {
      span[aria-hidden] {
        height: rem(12px);
        width: rem(12px);
        border-width: 1px;
      }
    }
  }

  .zg-tooltip--size-medium {
    .trigger {
      span[aria-hidden] {
        height: rem(20px);
        width: rem(20px);
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // position - top
  // ? ----------------------------------------------------------------------

  .zg-tooltip--position-top-left,
  .zg-tooltip--position-top-right,
  .zg-tooltip--position-top-center {
    .content {
      bottom: rem(32px);

      .tooltip-arrow {
        bottom: 0;
      }
    }
  }

  .zg-tooltip--position-top-left {
    .content {
      right: - rem(28px);

      .tooltip-arrow {
        right: rem(16px);
        transform: scaleX(-100%);
      }
    }
  }

  .zg-tooltip--position-top-right {
    .content {
      left: - rem(32px);

      .tooltip-arrow {
        left: rem(16px);
      }
    }
  }

  .zg-tooltip--position-top-center {
    .content {
      left: calc(50% + 16px);
      transform: translateX(-50%);

      .tooltip-arrow {
        left: 50%;
        transform: translateX(-50%) scaleX(-100%);
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // position - bottom
  // ? ----------------------------------------------------------------------

  .zg-tooltip--position-bottom-left,
  .zg-tooltip--position-bottom-right,
  .zg-tooltip--position-bottom-center {
    .content {
      top: rem(32px);

      .tooltip-arrow {
        top: 0;
      }
    }
  }

  .zg-tooltip--position-bottom-left {
    .content {
      right: - rem(28px);

      .tooltip-arrow {
        right: rem(16px);
        transform: scale(-100%, -100%);
      }
    }
  }

  .zg-tooltip--position-bottom-right {
    .content {
      left: - rem(32px);

      .tooltip-arrow {
        left: rem(16px);
        transform: scaleY(-100%);
      }
    }
  }

  .zg-tooltip--position-bottom-center {
    .content {
      left: calc(50% + 16px);
      transform: translateX(-50%);

      .tooltip-arrow {
        left: 50%;
        transform: translateX(-50%) scale(-100%, -100%);
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // position - left
  // ? ----------------------------------------------------------------------

  .zg-tooltip--position-left-top,
  .zg-tooltip--position-left-bottom,
  .zg-tooltip--position-left-center {
    .content {
      right: rem(36px);

      .tooltip-arrow {
        right: - rem(6px);
        transform: rotate(270deg);
      }
    }
  }

  .zg-tooltip--position-left-top {
    .content {
      bottom: - rem(36px);

      .tooltip-arrow {
        bottom: rem(28px);
      }
    }
  }

  .zg-tooltip--position-left-bottom {
    .content {
      top: - rem(10px);

      .tooltip-arrow {
        top: rem(28px);
      }
    }
  }

  .zg-tooltip--position-left-center {
    .content {
      top: calc(50% + 10px);
      transform: translateY(-50%);

      .tooltip-arrow {
        top: 50%;
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // position - right
  // ? ----------------------------------------------------------------------

  .zg-tooltip--position-right-top,
  .zg-tooltip--position-right-bottom,
  .zg-tooltip--position-right-center {
    .content {
      left: rem(36px);

      .tooltip-arrow {
        left: - rem(6px);
        transform: rotate(90deg) scaleX(-100%);
      }
    }
  }

  .zg-tooltip--position-right-top {
    .content {
      bottom: - rem(36px);

      .tooltip-arrow {
        bottom: rem(28px);
      }
    }
  }

  .zg-tooltip--position-right-bottom {
    .content {
      top: - rem(8px);

      .tooltip-arrow {
        top: rem(28px);
      }
    }
  }

  .zg-tooltip--position-right-center {
    .content {
      top: calc(50% + 12px);
      transform: translateY(-50%);

      .tooltip-arrow {
        top: 50%;
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // background
  // ? ----------------------------------------------------------------------

  .zg-tooltip--background-secondary-500 {
    .content {
      color: color(black);
      background-color: color(secondary-500);

      .tooltip-arrow ::v-deep svg {
        color: color(secondary-500);
      }
    }
  }

  .zg-tooltip--background-beige {
    .content {
      color: color(black);
      background-color: color(beige);

      .tooltip-arrow ::v-deep svg {
        color: color(beige);
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // input
  // ? ----------------------------------------------------------------------

  .zg-tooltip--input,
  .zg-tooltip--input-with-trigger {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    flex: 0;
    visibility: hidden;

    .content {
      top: auto;
      bottom: calc(100% + 12px);
      left: - rem(18px);
      right: - rem(18px);
      min-width: 0;
      height: auto;
      visibility: visible;

      .message {
        width: 100%;
      }
    }
  }

  .zg-tooltip--input-without-trigger {
    .content {
      bottom: calc(100% + 16px);

      .tooltip-arrow {
        left: 50%;
        transform: translateX(-50%) scaleX(-100%);
      }
    }
  }

  .zg-tooltip--input-with-trigger {
    .trigger {
      position: absolute;
      bottom: 0;
      right: 0;
      width: rem(40px);
      z-index: 1;
    }
  }

  // ? ----------------------------------------------------------------------
  // single line
  // ? ----------------------------------------------------------------------

  .zg-tooltip--single-line {
    .content {
      min-width: auto;

      .message {
        display: flex;
        flex-direction: row-reverse;
        align-items: center;
        justify-content: center;
        white-space: nowrap;
        padding: rhythm(xsmall) rem(10px) rhythm(xsmall) rhythm(small);

        p {
          display: inline;
          white-space: nowrap;
          line-height: rem(14px);
        }
      }

      .close {
        position: relative;
        top: unset;
        left: unset;
        right: unset;
        margin-left: rem(4px);
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // black trigger
  // ? ----------------------------------------------------------------------

  .zg-tooltip--black-trigger {
    .trigger {
      span[aria-hidden] {
        color: color(black);
        border-color: color(black);
      }

      ::v-deep svg {
        color: color(black);
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // animation
  // ? ----------------------------------------------------------------------

  .fade-down-enter-active,
  .fade-down-leave-active {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  .fade-down-enter,
  .fade-down-leave-to {
    opacity: 0;
    transform: translateY(rhythm(small)) scale(0.95);
  }

  @keyframes pulse {
    0% {
      opacity: 0;
      transform: scale(0.9);
    }

    50% {
      opacity: 1;
      transform: scale(1.4);
    }

    100% {
      opacity: 0;
      transform: scale(0.9);
    }
  }
</style>
